import type { AdminUser } from '../types';
import { Icon, Avatar, StatusBadge, TierBadge, Meter, fmt } from '../atoms';
import { Sparkline } from '../charts';
import { FLAGS, tokenSeries } from '../data';

export function UserDrawer({ user, onClose, onAction }: {
  user: AdminUser;
  onClose: () => void;
  onAction: (type: string, u: AdminUser) => void;
}) {
  const u = user;
  const tp = fmt.pct(u.tokens, u.capTokens);
  const cp = fmt.pct(u.cost, u.capCost);
  const dis = u.status === 'disabled';
  const spark = tokenSeries.map((v, i) => v * (0.4 + ((u.id.charCodeAt(2) + i) % 7) / 10));

  const stat = (label: string, value: string, small?: string, foot?: string) => (
    <div className="stat">
      <div className="l">{label}</div>
      <div className="v">{value}{small && <small> {small}</small>}</div>
      {foot && <div className="foot">{foot}</div>}
    </div>
  );

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="drawer" role="dialog">
        <div className="dr-top">
          <Avatar size={52} hue={u.hue} initials={u.initials} dis={dis} />
          <div className="who">
            <h2>{u.name}</h2>
            <div className="m">{u.email}</div>
            <div className="badges">
              <StatusBadge status={u.status} />
              <TierBadge tier={u.tier} />
              {u.flags.length > 0 && (
                <span className={'badge ' + (u.flags.some(f => FLAGS[f].sev === 'bad') ? 'bad' : 'warn')}>
                  <span className="d" />{u.flags.length} flag{u.flags.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <button className="dr-x" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="dr-scroll">
          <div className="dr-lbl">AI budget · this month</div>
          <div className="card" style={{ padding: '16px 16px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <span className="mono" style={{ fontSize: 15, fontWeight: 700 }}>{fmt.cost(u.cost)} spent</span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>cap {fmt.cost(u.capCost)}</span>
            </div>
            <Meter pct={cp} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '14px 0 8px' }}>
              <span className="mono" style={{ fontSize: 15, fontWeight: 700 }}>{fmt.tokens(u.tokens)} tokens</span>
              <span className="mono" style={{ fontSize: 12, color: 'var(--faint)' }}>cap {fmt.tokens(u.capTokens)}</span>
            </div>
            <Meter pct={tp} />
            {u.tokens > 0 && (
              <div style={{ marginTop: 14, opacity: 0.9 }}>
                <Sparkline data={spark} width={456} height={40} id={u.id} color="var(--accent)" />
              </div>
            )}
          </div>

          <div className="dr-lbl">Usage & cost</div>
          <div className="statgrid">
            {stat('Requests', fmt.int(u.reqs), '', 'lifetime API calls')}
            {stat('Cost baseline', fmt.cost(u.baseline), '/mo', '30-day rolling avg')}
            {stat('Failed logins', String(u.fails), '', u.fails > 4 ? 'above threshold' : 'within normal')}
            {stat('Member since', u.joined, '', 'last active ' + u.last)}
          </div>

          <div className="dr-lbl">Storage footprint</div>
          <div className="card" style={{ padding: '15px 16px' }}>
            <div className="stbar" style={{ marginBottom: 12 }}>
              <span className="db" style={{ width: (u.db + u.s3 ? u.db / (u.db + u.s3) * 100 : 0) + '%' }} />
              <span className="s3" style={{ width: (u.db + u.s3 ? u.s3 / (u.db + u.s3) * 100 : 0) + '%' }} />
            </div>
            <div style={{ display: 'flex', gap: 22 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--info)' }} />
                  <span style={{ fontSize: 12.5, color: 'var(--soft)' }}>DynamoDB</span>
                </div>
                <div className="mono" style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{fmt.mb(u.db)}</div>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--accent)' }} />
                  <span style={{ fontSize: 12.5, color: 'var(--soft)' }}>S3 (attachments)</span>
                </div>
                <div className="mono" style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{fmt.mb(u.s3)}</div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: 12.5, color: 'var(--soft)' }}>Total</div>
                <div className="mono" style={{ fontSize: 17, fontWeight: 700, marginTop: 4 }}>{fmt.mb(u.db + u.s3)}</div>
              </div>
            </div>
          </div>

          {u.flags.length > 0 && <div className="dr-lbl">Misuse signals</div>}
          {u.flags.length > 0 && (
            <div className="card" style={{ padding: '4px 16px' }}>
              {u.flags.map(f => {
                const meta = FLAGS[f];
                return (
                  <div className="dr-flag" key={f}>
                    <span className={'ic ' + meta.sev}>
                      <Icon name={meta.sev === 'bad' ? 'alert' : 'gauge'} size={16} />
                    </span>
                    <div className="tx">
                      <div className="t">{meta.label}</div>
                      <div className="d">{meta.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="dr-acts">
          {u.status === 'pending' && (
            <button className="btn pri" onClick={() => onAction('approve', u)}>
              <Icon name="check" size={15} />Approve
            </button>
          )}
          <button className="btn" onClick={() => onAction('caps', u)}>
            <Icon name="sliders" size={15} />Adjust caps
          </button>
          <button className="btn" onClick={() => onAction('reset', u)}>
            <Icon name="key" size={15} />Reset password
          </button>
          {u.flags.length > 0 && (
            <button className="btn" onClick={() => onAction('investigate', u)}>
              <Icon name="eye" size={15} />Investigate
            </button>
          )}
          {dis
            ? <button className="btn" onClick={() => onAction('enable', u)}
                style={{ marginLeft: 'auto', color: 'var(--ok)', borderColor: 'var(--ok-line)', background: 'var(--ok-soft)' }}>
                <Icon name="power" size={15} />Re-enable
              </button>
            : u.status !== 'pending' && (
              <button className="btn bad" onClick={() => onAction('disable', u)} style={{ marginLeft: 'auto' }}>
                <Icon name="ban" size={15} />Disable
              </button>
            )}
        </div>
      </div>
    </>
  );
}
